import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  CarpetPricingCatalogItem,
  CarpetPricingCategory,
  BreakdownSubItem,
  BreakdownSubItemInput,
} from '@jobos/shared';
import { ServiceTierPicker } from './ServiceTierPicker';
import { CategoryCard } from './CategoryCard';
import {
  buildSubItemsFromUI,
  computeTotals,
  groupCatalogByCategory,
  readUIFromSubItems,
  type CarpetTotals,
  type CarpetUIState,
} from './carpet-helpers';

const money = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

function SummaryBar({ totals, footer }: { totals: CarpetTotals; footer?: ReactNode }) {
  const segments: { label: string; value: number }[] = [];
  if (totals.serviceTotal) segments.push({ label: 'Service', value: totals.serviceTotal });
  if (totals.itemsTotal) segments.push({ label: 'Items', value: totals.itemsTotal });
  if (totals.surchargesTotal)
    segments.push({ label: 'Surcharges', value: totals.surchargesTotal });
  if (totals.discountTotal)
    segments.push({ label: 'Discounts', value: -totals.discountTotal });

  return (
    <div className="sticky top-0 z-10 -mx-4 md:-mx-6 mb-4 border-b border-gray-200 bg-white/95 px-4 md:px-6 py-3 backdrop-blur">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-baseline gap-2">
          <span className="text-xs uppercase tracking-wide text-gray-500">Total</span>
          <span className="text-xl font-semibold tabular-nums text-gray-900">
            {money(totals.grandTotal)}
          </span>
        </div>
        {segments.length > 0 && (
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-gray-500">
            {segments.map((s) => (
              <span key={s.label}>
                <span className="text-gray-400">{s.label}</span>{' '}
                <span className="tabular-nums text-gray-700">{money(s.value)}</span>
              </span>
            ))}
          </div>
        )}
        {footer && <div className="ml-auto flex items-center gap-2">{footer}</div>}
      </div>
    </div>
  );
}

const CATEGORY_TITLES: Record<Exclude<CarpetPricingCategory, 'service_tier'>, string> = {
  stairs: 'Stairs',
  hallways: 'Hallways & Landings',
  rooms: 'Rooms',
  extras: 'Extras',
  surcharges: 'Surcharges',
};

const CATEGORY_ORDER: Exclude<CarpetPricingCategory, 'service_tier'>[] = [
  'stairs',
  'hallways',
  'rooms',
  'extras',
  'surcharges',
];

interface CarpetEditorProps {
  catalog: CarpetPricingCatalogItem[];
  initialSubItems: BreakdownSubItem[];
  onChange: (subItems: BreakdownSubItemInput[]) => void;
  footer?: ReactNode;
  readOnly?: boolean;
}

export function CarpetEditor({
  catalog,
  initialSubItems,
  onChange,
  footer,
  readOnly = false,
}: CarpetEditorProps) {
  const grouped = useMemo(() => groupCatalogByCategory(catalog), [catalog]);
  const [state, setState] = useState<CarpetUIState>(() =>
    readUIFromSubItems(initialSubItems, catalog),
  );

  // Re-seed when the underlying breakdown changes (e.g. after save+refresh).
  // Compares by serialized identity to avoid infinite loops.
  const initialKey = useMemo(
    () => JSON.stringify(initialSubItems.map((s) => [s.id, s.type, s.hours, s.note])),
    [initialSubItems],
  );
  useEffect(() => {
    setState(readUIFromSubItems(initialSubItems, catalog));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey, catalog]);

  const totals = useMemo(() => computeTotals(state, catalog), [state, catalog]);

  const emit = (next: CarpetUIState) => {
    if (readOnly) return;
    setState(next);
    onChange(buildSubItemsFromUI(next, catalog));
  };

  const handleTierChange = (tierKey: string | null) =>
    emit({ ...state, tierKey });

  const handleQty = (key: string, qty: number) => {
    const rowsByKey = { ...state.rowsByKey };
    if (qty <= 0) {
      delete rowsByKey[key];
    } else {
      rowsByKey[key] = { qty, discount: state.rowsByKey[key]?.discount ?? 0 };
    }
    emit({ ...state, rowsByKey });
  };

  const handleDiscount = (key: string, discount: number) => {
    const rowsByKey = { ...state.rowsByKey };
    const existing = rowsByKey[key] ?? { qty: 0, discount: 0 };
    rowsByKey[key] = { ...existing, discount };
    emit({ ...state, rowsByKey });
  };

  return (
    <div className="@container">
      <SummaryBar totals={totals} footer={footer} />

      <div className="space-y-4">
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Service tier
          </h2>
          <ServiceTierPicker
            items={grouped.service_tier}
            value={state.tierKey}
            onChange={handleTierChange}
            readOnly={readOnly}
          />
        </section>

        <div className="grid gap-4 @2xl:grid-cols-2">
          {CATEGORY_ORDER.map((cat) => {
            const items = grouped[cat];
            if (items.length === 0) return null;
            return (
              <CategoryCard
                key={cat}
                title={CATEGORY_TITLES[cat]}
                items={items}
                rows={state.rowsByKey}
                onQtyChange={handleQty}
                onDiscountChange={handleDiscount}
                readOnly={readOnly}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
